<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <h4 class="my-4">НИР</h4>

      <CardAutocompleteRow
        v-model="grant.grantBody.nirType"
        :items="nirTypeList"
        item-value="id"
        item-text="name"
        label="Тип НИР"
        placeholder="Выберите тип НИР"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.nirType"
        @input="
          $v.grant.grantBody.nirType.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        v-model="grant.commonWork.workName"
        label="Название темы НИР"
        placeholder="Добавьте название темы НИР"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.workName"
        @input="
          $v.grant.commonWork.workName.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        v-model="grant.grantBody.contestName"
        label="Название конкурса"
        placeholder="Добавьте название конкурса"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.contestName"
        @input="
          $v.grant.grantBody.contestName.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        v-model="grant.grantBody.nirNumber"
        label="Номер"
        placeholder="Добавьте номер"
        maxlength="10"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.nirNumber"
        @input="
          $v.grant.grantBody.nirNumber.$touch()
          updateGrant()
        "
      />

      <CardDateRow
        v-model="grant.fromDate"
        label="Дата начала НИР"
        placeholder="Добавьте дата начала НИР"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.fromDate"
        @input="
          $v.grant.fromDate.$touch()
          updateGrant()
        "
      />

      <CardDateRow
        v-model="grant.toDate"
        label="Дата окончания НИР"
        placeholder="Добавьте дата окончания НИР"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.toDate"
        @input="
          $v.grant.toDate.$touch()
          updateGrant()
        "
      />

      <h4 class="my-4 mt-10">Финансирование</h4>

      <CardAutocompleteRow
        v-model="grant.grantBody.nirLevel"
        :items="nirLevelList"
        item-value="id"
        item-text="name"
        label="Уровень НИР"
        placeholder="Выберите уровень НИР"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.nirLevel"
        @input="
          $v.grant.grantBody.nirLevel.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        v-model="grant.grantBody.fundingOrganization"
        label="Грантодатель"
        placeholder="Добавьте грантодателя"
        :disabled="isShowMode"
        :error-messages="errorMessages.fundingOrganization"
        @input="
          $v.grant.grantBody.fundingOrganization.$touch()
          updateGrant()
        "
      />

      <CardAutocompleteRow
        v-model="grant.grantBody.fundingRecipient"
        :items="fundingRecipientList"
        item-value="id"
        item-text="name"
        label="Получатель финансирования"
        placeholder="Выберите получатель финансирования"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.fundingRecipient"
        @input="
          $v.grant.grantBody.fundingRecipient.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        v-if="isFundingRecipientSZGMU"
        v-model="grant.executor"
        label="Ответственный исполнитель"
        placeholder="Добавьте ответственного исполнителя"
        :disabled="isShowMode"
        :required="isFundingRecipientSZGMU && !isShowMode"
        :error-messages="errorMessages.executor"
        @input="
          $v.grant.executor.$touch()
          updateGrant()
        "
      />

      <CardFieldRow
        label="Объем финансирования на год"
        placeholder="Добавьте объем финансирования на год"
        v-model="grant.grantBody.fundingSum"
        type="number"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.fundingSum"
        @input="
          $v.grant.grantBody.fundingSum.$touch()
          updateGrant()
        "
      />

      <CardRow>
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="grant.universityInvolvment"
          label="Участие университета в выполнении НИР"
          dense
          hide-details
          :disabled="isShowMode"
          @change="updateGrant()"
        />
      </CardRow>

      <div
        v-if="!isShowMode"
        class="d-flex"
      >
        <UIAlert title="Необходимо добавить ссылку и/или прикрепить подтверждающий документ" />
      </div>

      <CardFieldRow
        v-model="grant.link"
        label="Ссылка на подтверждающий документ"
        placeholder="Добавьте ссылку на подтверждающий документ"
        hide-border-bottom
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.link"
        @input="
          $v.grant.link.$touch()
          updateGrant()
        "
      />

      <CardRow
        label="Подтверждающие материалы"
        class="mt-0"
      >
        <UIUploadFile
          acceptFileFormats=".pdf, .jpeg, .jpg, .doc, .docx"
          button-text="Добавить подтверждающие материалы"
          :maxFileWeightInMb="5"
          :is-show-mode="isShowMode"
          :multiple="true"
          :maxFiles="10"
          :fileList="linkFileList"
          @uploadFiles="uploadFiles"
          @removeFile="removeFile"
        />
      </CardRow>

      <CardTextAreaRow
        v-model="grant.note"
        label="Примечание"
        placeholder="Добавьте примечание"
        :disabled="isShowMode"
        :error-messages="errorMessages.note"
        @input="
          $v.grant.note.$touch()
          updateGrant()
        "
      />

      <h4 class="my-4 mt-10">Совместно с какими научными подразделениями Университета выполняется НИР</h4>

      <CardAutocompleteRow
        v-model="grant.commonWork.scientDepartments"
        :items="scientDepartmentList"
        item-value="id"
        item-text="name"
        label="Подразделения"
        placeholder="Выберите подразделения"
        multiple
        hide-border-bottom
        :disabled="isShowMode"
        :no-filter="false"
        @input="updateGrant()"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Grant } from '@/models'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '../../ui/row/CardFieldRow'
import { setValidateMessages, checkDate, compareTwoDates } from '../../../scripts/validation'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import CardTextAreaRow from '../../ui/row/CardTextAreaRow'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import CardDateRow from '../../ui/row/CardDateRow'
import { mapGetters } from 'vuex'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'
import UIAlert from '@/components/ui/UIAlert.vue'

export default {
  components: { UIAlert, UIUploadFile, CardDateRow, CardAutocompleteRow, CardTextAreaRow, CardFieldRow, CardRow },

  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String,
    linkFileList: Array
  },

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    grant: new Grant(),

    fundingRecipientId: {
      SZGMU: 1,
      personalGrant: 2,
      otherOrganization: 3
    }
  }),

  validations() {
    return {
      grant: {
        commonWork: {
          workName: { required, maxLength: maxLength(1500) }
        },

        grantBody: {
          nirType: { required },
          contestName: { required, maxLength: maxLength(600) },
          nirNumber: { required, maxLength: maxLength(30) },
          nirLevel: { required },
          fundingOrganization: { maxLength: maxLength(300) },
          fundingRecipient: { required },
          fundingSum: { required, maxLength: maxLength(12) }
        },

        fromDate: { required, checkDate },
        toDate: {
          required,
          checkDate,
          compareTwoDates
        },
        link: { required: requiredIf(() => !this.linkFileList.length), maxLength: maxLength(500) },
        note: { maxLength: maxLength(700) },
        executor: {
          required: requiredIf(() => this.isFundingRecipientSZGMU)
        }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['nirTypeList', 'fundingRecipientList', 'scientDepartmentList', 'nirLevelList']),

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.grant.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.grant[key])
      })

      Object.keys(this.$v.grant.grantBody.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.grant.grantBody[key])
      })

      Object.keys(this.$v.grant.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.grant.commonWork[key])
      })

      return allErrors
    },

    isFundingRecipientSZGMU() {
      return this.grant.grantBody.fundingRecipient?.id === this.fundingRecipientId.SZGMU
    }
  },

  methods: {
    updateGrant() {
      this.$emit('update:selected-item', new Grant(this.grant.getForAPI()))
      this.updateInvalid()
    },

    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    uploadFiles(files) {
      this.$emit('uploadLinkFiles', files)
      this.updateGrant()
    },

    removeFile(index) {
      this.$emit('removeLinkFile', index)
      this.updateGrant()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.grant = new Grant(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
